import React from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useQuery } from 'react-query';
import {
  Box,
  Grid,
  Paper,
  Typography,
  CircularProgress,
  Alert,
  Chip,
} from '@mui/material';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { hasPermission } from '../utils/permissions';
import { navigateToArtworksSearch } from '../utils/listViewState';

export default function Dashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { data: stats, isLoading, error } = useQuery(
    ['statistics', user?.id, user?.allowed_collection_codes],
    async () => {
      const response = await axios.get('/api/reports/statistics');
      return response.data;
    },
    { enabled: hasPermission(user, 'view_reports') }
  );

  if (user?.role === 'visitor') {
    return <Navigate to="/artworks" replace />;
  }

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error">Ошибка загрузки статистики</Alert>
    );
  }

  const handleTagClick = (tagName) => {
    navigateToArtworksSearch(navigate, `#${tagName}`);
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Панель управления
      </Typography>
      <Typography variant="body1" color="text.secondary" gutterBottom>
        Быстрая статистика фондов
      </Typography>

      <Grid container spacing={3} sx={{ mt: 2 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" sx={{ color: '#57726C' }}>
              Всего произведений
            </Typography>
            <Typography variant="h4" sx={{ color: '#68998C' }}>
              {stats?.total_artworks || 0}
            </Typography>
          </Paper>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" sx={{ color: '#57726C' }}>
              В экспозиции
            </Typography>
            <Typography variant="h4" sx={{ color: '#68998C' }}>
              {stats?.statuses?.['в экспозиции'] || 0}
            </Typography>
          </Paper>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" sx={{ color: '#57726C' }}>
              В хранении
            </Typography>
            <Typography variant="h4" sx={{ color: '#68998C' }}>
              {stats?.statuses?.['на хранении'] || 0}
            </Typography>
          </Paper>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" sx={{ color: '#57726C' }}>
              В архиве
            </Typography>
            <Typography variant="h4" sx={{ color: '#68998C' }}>
              {(stats?.statuses?.['продано'] || 0) + (stats?.statuses?.['исключено'] || 0)}
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      <Grid container spacing={3} sx={{ mt: 2 }} alignItems="stretch">
        <Grid item xs={12} md={4} sx={{ display: 'flex' }}>
          <Paper
            sx={{
              p: 2,
              width: '100%',
              height: 420,
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Typography variant="h6" gutterBottom sx={{ flexShrink: 0 }}>
              Произведения по коллекциям
            </Typography>
            <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
              {stats?.collections &&
                Object.entries(stats.collections).map(([code, data]) => (
                  <Box key={code} sx={{ mb: 1 }}>
                    <Typography variant="body2">
                      {data.name}: {data.count}
                    </Typography>
                  </Box>
                ))}
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} md={4} sx={{ display: 'flex' }}>
          <Paper
            sx={{
              p: 2,
              width: '100%',
              height: 420,
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Typography variant="h6" gutterBottom sx={{ flexShrink: 0 }}>
              Авторы
            </Typography>
            <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
              {stats?.top_authors?.length ? (
                stats.top_authors.map((author) => (
                  <Box key={author.author_id} sx={{ mb: 1 }}>
                    <Typography variant="body2">
                      {author.author_name}: {author.count} произведений
                    </Typography>
                  </Box>
                ))
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Нет авторов
                </Typography>
              )}
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} md={4} sx={{ display: 'flex' }}>
          <Paper
            sx={{
              p: 2,
              width: '100%',
              height: 420,
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Typography variant="h6" gutterBottom sx={{ flexShrink: 0 }}>
              Теги
            </Typography>
            <Box
              sx={{
                flex: 1,
                minHeight: 0,
                overflowY: 'auto',
                display: 'flex',
                flexWrap: 'wrap',
                alignContent: 'flex-start',
                gap: 1,
              }}
            >
              {stats?.tags?.length ? (
                stats.tags.map((tag) => (
                  <Chip
                    key={tag.tag_id}
                    label={`#${tag.tag_name}${tag.count ? ` (${tag.count})` : ''}`}
                    onClick={() => handleTagClick(tag.tag_name)}
                    sx={{
                      bgcolor: tag.color || '#DDE8E4',
                      color: '#3D4F4A',
                      fontWeight: 500,
                      border: '1px solid rgba(61, 79, 74, 0.12)',
                      cursor: 'pointer',
                      '&:hover': {
                        bgcolor: tag.color || '#DDE8E4',
                        filter: 'brightness(0.96)',
                      },
                    }}
                  />
                ))
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Нет тегов
                </Typography>
              )}
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}

